/*
 * The following code uses Cloud Storage's XML API to upload a file in chunks.
 * It is based on the following documentation: https://cloud.google.com/storage/docs/xml-api
 */

import { createError } from '@common/neo.tax';

import { wait } from '@neo.tax/shared/utils/function.utils';
import { SyntheticEvent } from '@neo.tax/shared/utils/user-inactivity.utils';

const MIN_RETRY_BACKOFF_MS = 1000; // 1 second
const MAX_RETRY_BACKOFF_MS = 32 * 1000; // 32 seconds

// The chunk size should be a multiple of 256 KiB (256 x 1024 bytes)
// Using 8 MB as a compromise allowing for fast uploads and low error rates for slower connections
// TODO this can be increased dynamically based on uploads status – consider using exponential grow and decay of
// the first coefficient below:
const CHUNK_SIZE_BYTES = 8 * 4 * 256 * 1024;

/**
 * Initiates a resumable upload and fetches the location URL to upload the file to.
 *
 * See docs: https://cloud.google.com/storage/docs/xml-api/post-object-resumable
 */
export const fetchAuthenticatedLocationUrl = async (signedUrl: string, contentType: string): Promise<string> => {
  // initiate the resumable upload, no auth required when using a signed URL
  const resumableSession = await fetch(signedUrl, {
    method: 'POST',
    headers: {
      'Content-Type': contentType, // The MIME type of the object to be uploaded.
      'Content-Length': '0', // For initiating a resumable upload, this value is 0.
      'X-Goog-Resumable': 'start', // Indicates that this request initiates the resumable upload. This header must use the value `start`.
    },
  });

  if (!resumableSession.ok) {
    throw createError(
      'ResumableUploadError',
      `Unable to start resumable upload: ${resumableSession.status} ${await resumableSession.text()}`,
    );
  }

  // Endpoint to which we should upload the file chunks
  const location = resumableSession.headers.get('location');
  if (!location) {
    throw createError('ResumableUploadError', 'Unable to start resumable upload: Missing location header.');
  }

  return location;
};

/**
 * See docs: https://cloud.google.com/storage/docs/performing-resumable-uploads#cancel-upload
 *
 * @param locationUrl - The location URL to cancel the resumable upload.
 */
export const cancelResumableUpload = async (locationUrl: string): Promise<void> => {
  const resumableSession = await fetch(locationUrl, {
    method: 'DELETE',
    headers: {
      'Content-Length': '0', // For canceling a resumable upload, this value is 0.
    },
  });

  if (resumableSession.status !== 204) {
    throw createError(
      'ResumableUploadError',
      `Unable to cancel resumable upload: ${resumableSession.status} ${await resumableSession.text()}`,
    );
  }
};

/**
 * Uploads a file in chunks to the given location URL.
 *
 * @param locationUrl - The location URL to upload the file to.
 * @param file - The file to upload.
 * @param setPercentCompleted - Optional callback to update the upload progress. The callback should return `true` to continue
 * the upload. Use this to cancel the upload if needed.
 */
export const uploadFile = async (
  locationUrl: string,
  file: File,
  setPercentCompleted?: (percent: number) => PromiseLike<boolean> | boolean,
): Promise<void> => {
  const totalSizeBytes = file.size;
  let uploadedBytes = 0;
  let retryBackoffMs = MIN_RETRY_BACKOFF_MS;

  // TODO performance can be significantly improved by using a pool of concurrent requests. However, the implementation
  // is more complex and requires careful handling of the Range header in the response and updating the queued chunks.
  // This is because the server may not accept all bytes in the last chunk, so we need to dynamically adjust chunk boundaries.
  while (uploadedBytes < totalSizeBytes) {
    // Keep user active during resumable uploads
    window.dispatchEvent(new Event(SyntheticEvent.UPLOAD_CHUNK_ATTEMPT));

    // Calculate the byte range to upload
    const chunk = file.slice(uploadedBytes, uploadedBytes + CHUNK_SIZE_BYTES);
    const chunkSizeBytes = chunk.size;
    const endByte = uploadedBytes + chunkSizeBytes - 1;

    let response: Response;
    try {
      response = await fetch(locationUrl, {
        method: 'PUT',
        headers: {
          'Content-Range': `bytes ${uploadedBytes}-${endByte}/${totalSizeBytes}`,
          'Content-Length': chunkSizeBytes.toString(),
        },
        body: chunk,
      });
    } catch (error) {
      if (!(error instanceof TypeError && error.message.includes('Failed to fetch'))) {
        throw error;
      }
      console.log(`${file.name}: Network error, retrying in ${retryBackoffMs}ms`);
      retryBackoffMs = Math.min(retryBackoffMs * 2, MAX_RETRY_BACKOFF_MS);
      await wait(retryBackoffMs);
      continue;
    }

    retryBackoffMs = MIN_RETRY_BACKOFF_MS;

    // If the request succeeds, the server responds with 308 Resume Incomplete. The response contains a Range header.
    if (response.status === 308) {
      // Handle redirection if needed
      const newLocation = response.headers.get('location');
      if (typeof newLocation === 'string' && newLocation.length > 0) {
        locationUrl = newLocation;
      }

      // Check the Range header to see what has been uploaded, it may not have fully accepted all bytes in the last chunk
      const range = response.headers.get('Range');
      if (!range) {
        throw createError('ResumableUploadError', 'Missing range header in 308 response.');
      }
      const matches = range.match(/bytes=0-(\d+)/);
      uploadedBytes = matches ? parseInt(matches[1], 10) + 1 : uploadedBytes;
    } else if (response.status === 200 || response.status === 201) {
      uploadedBytes = totalSizeBytes;
      console.log(`${file.name}: Upload complete`);
    } else if (response.status === 204) {
      console.log(`${file.name}: Upload cancelled in Cloud Storage`);
      return;
    } else if (response.status === 503) {
      // covering an edge case where an upload in canceled while the last chunk is being uploaded
      const shouldContinue = await setPercentCompleted?.(0);
      if (shouldContinue) {
        throw createError(
          'ResumableUploadError',
          `Unexpected response status: ${response.status} ${await response.text()}`,
        );
      }
      console.log(`${file.name}: Upload cancelled by user.`);
      return;
    } else {
      throw createError(
        'ResumableUploadError',
        `Unexpected response status: ${response.status} ${await response.text()}`,
      );
    }

    const uploadPercent = Math.floor((uploadedBytes / totalSizeBytes) * 10000) / 100;
    if (uploadedBytes !== totalSizeBytes) {
      console.log(`${file.name}: Uploaded ${uploadedBytes} of ${totalSizeBytes}: ${uploadPercent.toFixed(2)}%`);
    }
    const shouldContinue = await setPercentCompleted?.(uploadPercent);
    if (shouldContinue === false) {
      console.log(`${file.name}: Upload cancelled by user.`);
      await cancelResumableUpload(locationUrl);
      return;
    }
  }
};
